import { supabase } from '../lib/supabase';
import { CatalogItem } from '../types';
import { v4 as uuidv4 } from 'uuid';

// Nom de la table où seront stockés les éléments du catalogue
const CATALOG_TABLE = 'catalog_items';

/**
 * Convertit un objet CatalogItem avec camelCase en format snake_case pour PostgreSQL
 */
const catalogItemToPgFormat = (item: Partial<CatalogItem>): Record<string, any> => {
  const pgItem: Record<string, any> = {};
  
  if (item.id) pgItem.id = item.id;
  if (item.title !== undefined) pgItem.title = item.title;
  if (item.description !== undefined) pgItem.description = item.description;
  if (item.image !== undefined) pgItem.image = item.image;
  if (item.type !== undefined) pgItem.type = item.type;
  if (item.designer !== undefined) pgItem.designer = item.designer;
  if (item.material !== undefined) pgItem.material = item.material;
  if (item.dimensions !== undefined) pgItem.dimensions = item.dimensions;
  if (item.year !== undefined) pgItem.year = item.year;
  if (item.isPublished !== undefined) pgItem.is_published = item.isPublished;
  if (item.createdAt) pgItem.created_at = item.createdAt;
  
  return pgItem;
};

/**
 * Convertit un objet de la base de données avec snake_case en format CatalogItem avec camelCase
 */
const pgToCatalogItemFormat = (pgItem: Record<string, any>): CatalogItem => {
  return {
    id: pgItem.id,
    title: pgItem.title,
    description: pgItem.description || '',
    image: pgItem.image || '',
    type: pgItem.type,
    designer: pgItem.designer,
    material: pgItem.material,
    dimensions: pgItem.dimensions,
    year: pgItem.year,
    isPublished: pgItem.is_published,
    createdAt: pgItem.created_at
  };
};

/**
 * Récupère tous les éléments du catalogue
 */
export const getAllCatalogItems = async (): Promise<CatalogItem[]> => {
  try {
    const { data, error } = await supabase
      .from(CATALOG_TABLE)
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data.map(pgToCatalogItemFormat);
  } catch (error) {
    console.error('Erreur lors de la récupération des éléments du catalogue:', error);
    return [];
  }
};

/**
 * Récupère les éléments publiés du catalogue
 */
export const getPublishedCatalogItems = async (): Promise<CatalogItem[]> => {
  try {
    const { data, error } = await supabase
      .from(CATALOG_TABLE)
      .select('*')
      .eq('is_published', true)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data.map(pgToCatalogItemFormat);
  } catch (error) {
    console.error('Erreur lors de la récupération des éléments publiés du catalogue:', error);
    return [];
  }
};

/**
 * Récupère un élément du catalogue par son ID
 */
export const getCatalogItemById = async (id: string): Promise<CatalogItem | null> => {
  try {
    const { data, error } = await supabase
      .from(CATALOG_TABLE)
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    if (!data) return null;
    
    return pgToCatalogItemFormat(data);
  } catch (error) {
    console.error(`Erreur lors de la récupération de l'élément du catalogue ${id}:`, error);
    return null;
  }
};

/**
 * Crée un nouvel élément du catalogue
 */
export const createCatalogItem = async (item: Omit<CatalogItem, 'id' | 'createdAt'>): Promise<CatalogItem> => {
  try {
    const newItem = {
      id: uuidv4(),
      ...item,
      createdAt: new Date().toISOString(),
      isPublished: item.isPublished !== undefined ? item.isPublished : true
    };
    
    const { error } = await supabase
      .from(CATALOG_TABLE)
      .insert([catalogItemToPgFormat(newItem)]);
    
    if (error) throw error;
    
    return newItem;
  } catch (error) {
    console.error('Erreur lors de la création de l\'élément du catalogue:', error);
    throw error;
  }
};

/**
 * Met à jour un élément du catalogue existant
 */
export const updateCatalogItem = async (id: string, item: Partial<CatalogItem>): Promise<CatalogItem> => {
  try {
    const { error } = await supabase
      .from(CATALOG_TABLE)
      .update(catalogItemToPgFormat(item))
      .eq('id', id);
    
    if (error) throw error;
    
    const updatedItem = await getCatalogItemById(id);
    if (!updatedItem) throw new Error('Élément non trouvé après mise à jour');
    
    return updatedItem;
  } catch (error) {
    console.error(`Erreur lors de la mise à jour de l'élément du catalogue ${id}:`, error);
    throw error;
  }
};

/**
 * Supprime un élément du catalogue
 */
export const deleteCatalogItem = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from(CATALOG_TABLE)
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  } catch (error) {
    console.error(`Erreur lors de la suppression de l'élément du catalogue ${id}:`, error);
    throw error;
  }
};

/**
 * Télécharge une image pour un élément du catalogue
 */
export const uploadCatalogImage = async (file: File, itemId: string, itemTitle: string): Promise<string> => {
  try {
    if (!file) {
      console.error('Aucun fichier fourni pour l\'upload');
      throw new Error('Aucun fichier fourni');
    }

    console.log('Début de l\'upload de l\'image:', file.name, 'pour l\'élément:', itemId);
    
    // Formatage du titre pour le nom de fichier
    const formattedTitle = (itemTitle || 'item')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    
    // Créer un nom de fichier unique
    const fileExt = file.name.split('.').pop() || 'jpg';
    const fileName = `${formattedTitle}-${Date.now()}.${fileExt}`;
    
    // Créer le chemin du fichier
    const filePath = `catalog/${itemId}/${fileName}`;
    
    console.log('Chemin du fichier pour l\'upload:', filePath);
    
    // Upload du fichier
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('agence-interieurs')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });
    
    if (uploadError) {
      console.error('Erreur Supabase lors de l\'upload:', uploadError);
      throw uploadError;
    }
    
    console.log('Upload réussi:', uploadData);
    
    // Récupérer l'URL publique
    const { data } = supabase.storage
      .from('agence-interieurs')
      .getPublicUrl(filePath);
    
    if (!data || !data.publicUrl) {
      throw new Error('Impossible de récupérer l\'URL publique de l\'image');
    }
    
    console.log('URL publique générée:', data.publicUrl);
    return data.publicUrl;
  } catch (error) {
    console.error('Erreur lors de l\'upload de l\'image:', error);
    throw error;
  }
};

/**
 * Supprime une image d'un élément du catalogue
 */
export const deleteCatalogImage = async (imageUrl: string): Promise<void> => {
  try {
    // Extraire le chemin de l'image depuis l'URL complète
    const urlParts = imageUrl.split('/');
    const filePath = urlParts.slice(urlParts.indexOf('images') + 1).join('/');
    
    if (!filePath) {
      throw new Error('Impossible d\'extraire le chemin du fichier depuis l\'URL');
    }
    
    const { error } = await supabase.storage
      .from('agence-interieurs')
      .remove([filePath]);
    
    if (error) throw error;
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'image:', error);
    throw error;
  }
};
